import React from "react";
import { useTranslation } from "react-i18next";
import { fmtDateTime } from "../../lib/format";

const STATUS_TONE = {
  pending: { bg: "#e2e8f0", color: "#334155", icon: "⏳" },
  sending: { bg: "#e0f2fe", color: "#075985", icon: "…" },
  sent: { bg: "#dbeafe", color: "#1e40af", icon: "✓" },
  delivered: { bg: "#dcfce7", color: "#166534", icon: "✓✓" },
  delivery_delayed: { bg: "#fef3c7", color: "#92400e", icon: "!" },
  bounced: { bg: "#fee2e2", color: "#991b1b", icon: "×" },
  failed: { bg: "#fee2e2", color: "#991b1b", icon: "×" },
  complained: { bg: "#fce7f3", color: "#9d174d", icon: "▲" },
  suppressed: { bg: "#f3f4f6", color: "#374151", icon: "⊘" },
};

function StatusPill({ status, t }) {
  const tone = STATUS_TONE[status] || STATUS_TONE.pending;
  const label = t(`quotations.email_status.${status}`, { defaultValue: status });
  return (
    <span
      data-testid={`email-status-${status}`}
      aria-label={label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        background: tone.bg,
        color: tone.color,
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      <span aria-hidden="true">{tone.icon}</span>
      <span>{label}</span>
    </span>
  );
}

export default function EmailHistory({ items, loading, error, onResend }) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="qw-empty" data-testid="email-history-loading">
        {t("common.loading")}
      </div>
    );
  }
  if (error) {
    return (
      <div className="qw-error" data-testid="email-history-error">
        {error}
      </div>
    );
  }
  if (!items || items.length === 0) {
    return (
      <div className="qw-empty" data-testid="email-history-empty">
        {t("quotations.email_history_empty")}
      </div>
    );
  }
  return (
    <div style={{ display: "grid", gap: 8 }} data-testid="email-history-list">
      {items.map((it) => {
        const failureLabel = it.failure_code
          ? t(`quotations.email_failure_code.${it.failure_code}`, {
              defaultValue: it.failure_code,
            })
          : null;
        return (
          <div
            key={it.id}
            data-testid={`email-history-item-${it.id}`}
            className="qw-email-item"
            style={{
              padding: 12,
              border: "1px solid #e2e8f0",
              background: "#ffffff",
              borderRadius: 8,
            }}
          >
            <div
              className="hstack"
              style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}
            >
              <div style={{ minWidth: 0, flex: "1 1 160px" }}>
                <div className="qw-email-subject">{it.subject}</div>
                <div className="qw-email-recipient">
                  → {it.recipient_email}
                </div>
              </div>
              <div className="hstack" style={{ gap: 6 }}>
                <StatusPill status={it.status} t={t} />
                {it.test_mode && (
                  <span
                    data-testid={`email-test-mode-${it.id}`}
                    style={{
                      background: "#fef3c7",
                      color: "#92400e",
                      padding: "2px 8px",
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  >
                    {t("quotations.email_test_mode_badge")}
                  </span>
                )}
              </div>
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: "#64748b", overflowWrap: "anywhere" }}>
              <div>
                {t("quotations.email_dialog_from")}:{" "}
                {it.requested_by?.display_name || t("quotations.owner_user_fallback")}
                {" · "}
                {fmtDateTime(it.requested_at)}
              </div>
              {it.delivered_at && (
                <div>
                  {t("quotations.email_status.delivered")}: {fmtDateTime(it.delivered_at)}
                </div>
              )}
              {failureLabel && (
                <div style={{ color: "#991b1b" }}>{failureLabel}</div>
              )}
              {it.resend_of_email_id && (
                <div style={{ fontStyle: "italic" }}>
                  ↳ {t("quotations.email_resend")}
                </div>
              )}
            </div>
            {onResend &&
              ["sent", "delivered", "opened", "question"].indexOf(it.status) >= 0 && (
                <div className="hstack" style={{ marginTop: 8, justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    className="qw-btn sm"
                    onClick={() => onResend(it)}
                    data-testid={`email-resend-btn-${it.id}`}
                  >
                    {t("quotations.email_resend")}
                  </button>
                </div>
              )}
          </div>
        );
      })}
    </div>
  );
}
